import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { checkIsActive, KTSVG } from '../../../helpers'
import { allRoutes } from '../../../../app/routing/All_Routes'
import { constraintConfig } from '../../../../constraintConfig'

/** Icon paths aligned with the settings mega-menu mockup */
const ICONS = {
  settingsGear: '/media/icons/duotune/general/gen019.svg',
  alertCircle: '/media/icons/duotune/general/gen044.svg',
  pieChart: '/media/icons/duotune/graphs/gra010.svg',
  campaigns: '/media/icons/duotune/communication/com005.svg',
  toggle: '/media/icons/duotune/general/gen019.svg',
  cloudDownload: '/media/icons/duotune/files/fil021.svg',
  email: '/media/icons/duotune/communication/com011.svg',
  calendar: '/media/icons/duotune/general/gen014.svg',
  car: '/media/icons/duotune/general/gen062.svg',
  sync: '/media/icons/duotune/arrows/arr031.svg',
  briefcase: '/media/icons/duotune/finance/fin006.svg',
  creditCard: '/media/icons/duotune/finance/fin002.svg',
  layers: '/media/icons/duotune/abstract/abs027.svg',
  clipboard: '/media/icons/duotune/general/gen005.svg',
  grid: '/media/icons/duotune/general/gen025.svg',
  mapPin: '/media/icons/duotune/maps/map001.svg',
  rocket: '/media/icons/duotune/general/gen002.svg',
  searchUser: '/media/icons/duotune/general/gen004.svg',
  barChart: '/media/icons/duotune/graphs/gra006.svg',
  globe: '/media/icons/duotune/maps/map004.svg',
  mobile: '/media/icons/duotune/electronics/elc002.svg',
  fileMissing: '/media/icons/duotune/files/fil003.svg',
  fileAdd: '/media/icons/duotune/files/fil005.svg',
  merge: '/media/icons/duotune/general/merge.svg',
  shield: '/media/icons/duotune/general/gen049.svg',
  anomalyRadar: '/media/icons/duotune/graphs/gra011.svg',
  lightning: '/media/icons/duotune/electronics/elc005.svg',
} as const

type MenuLink = {
  to: string
  title: string
  description?: string
  icon?: string
}

type MenuGroup = {
  title: string
  items: MenuLink[]
}

type MenuColumn = {
  id: string
  title: string
  accentClass: string
  headerIcon: string
  links: MenuLink[]
  groups?: MenuGroup[]
}

type Props = {
  variant: 'orgAdmin' | 'superAdmin'
  roleID: number
  isSurveysModuleEnabled?: boolean | null
  isExpenseAnomaliesModuleEnabled?: boolean | null
}

const MegaLink: React.FC<MenuLink & { accentClass: string }> = ({
  to,
  title,
  description,
  icon,
  accentClass,
}) => {
  const { pathname } = useLocation()
  const active = checkIsActive(pathname, to)

  return (
    <Link
      to={to}
      className={clsx('ss-settings-mega__link', accentClass, { 'ss-settings-mega__link--active': active })}
      onClick={(e) => e.stopPropagation()}
    >
      {icon ? (
        <span className='ss-settings-mega__link-icon'>
          <KTSVG path={icon} className='svg-icon-3' />
        </span>
      ) : (
        <span className='ss-settings-mega__link-dot' aria-hidden='true' />
      )}
      <span className='ss-settings-mega__link-content'>
        <span className='ss-settings-mega__link-title'>{title}</span>
        {description ? <span className='ss-settings-mega__link-desc'>{description}</span> : null}
      </span>
      <span className='ss-settings-mega__link-chevron' aria-hidden='true'>
        <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-3' />
      </span>
    </Link>
  )
}

const MegaGroup: React.FC<{ group: MenuGroup; accentClass: string }> = ({ group, accentClass }) => (
  <div className='ss-settings-mega__group'>
    <div className='ss-settings-mega__group-title'>{group.title}</div>
    {group.items.map((item) => (
      <MegaLink key={item.to + item.title} {...item} accentClass={accentClass} />
    ))}
  </div>
)

const buildOrgAdminColumns = (
  roleID: number,
  isSurveysModuleEnabled?: boolean | null,
  isExpenseAnomaliesModuleEnabled?: boolean | null
): MenuColumn[] => {
  const showBudget = roleID !== constraintConfig.roleID.roleIDAdmin
  const showSurveys = isSurveysModuleEnabled !== false
  const showAnomalies = isExpenseAnomaliesModuleEnabled !== false

  const surveysGroup: MenuGroup | null = showSurveys
    ? {
        title: 'Surveys',
        items: [
          {
            to: '/settings/survey-dashboard',
            title: 'Survey Dashboard',
            description: 'View survey analytics and results',
            icon: ICONS.pieChart,
          },
          {
            to: '/settings/survey-campaigns',
            title: 'Survey Campaigns',
            description: 'Create and manage survey campaigns',
            icon: ICONS.campaigns,
          },
        ],
      }
    : null

  const workflowGroups: MenuGroup[] = showBudget
    ? [
        {
          title: 'Budget Allocation',
          items: [
            {
              to: '/budget-allocation/budget',
              title: 'User Flex Budget',
              description: 'Manage user flexible budget limits',
              icon: ICONS.briefcase,
            },
            {
              to: '/budget-allocation/category-budgeting',
              title: 'Category Budget Allocation',
              description: 'Allocate budgets across categories',
              icon: ICONS.creditCard,
            },
          ],
        },
      ]
    : []

  const anomalyLinks: MenuLink[] = [
    {
      to: '/settings/expense-violation',
      title: 'Expense Violation',
      description: 'Manage expense violation rules',
      icon: ICONS.alertCircle,
    },
  ]

  if (showAnomalies) {
    anomalyLinks.push({
      to: `${allRoutes.analytics}/anomalies`,
      title: 'Anomalies Detection',
      description: 'Identify unusual expense patterns and risks',
      icon: ICONS.anomalyRadar,
    })
  }

  return [
    {
      id: 'settings',
      title: 'Settings',
      accentClass: 'ss-settings-mega__column--settings',
      headerIcon: ICONS.settingsGear,
      links: [
        {
          to: '/settings/org-settings',
          title: 'Org Settings',
          description: 'Organization preferences and details',
          icon: ICONS.settingsGear,
        },
        {
          to: '/settings/user-notifications',
          title: 'Maintenance / User Notifications',
          description: 'System maintenance and user alerts',
          icon: ICONS.alertCircle,
        },
      ],
      groups: surveysGroup ? [surveysGroup] : [],
    },
    {
      id: 'app-logs',
      title: 'SS App Logs',
      accentClass: 'ss-settings-mega__column--logs',
      headerIcon: ICONS.cloudDownload,
      links: [
        {
          to: `/settings${allRoutes.requestLogs}`,
          title: 'Failed APIs / Integration Request Logs',
          description: 'View failed API and integration request logs',
          icon: ICONS.alertCircle,
        },
        {
          to: `/settings${allRoutes.logFiles}`,
          title: 'SS Trace Log Files',
          description: 'View system trace log files',
          icon: ICONS.cloudDownload,
        },
        {
          to: `/settings${allRoutes.emailLogs}`,
          title: 'SS Email Logs',
          description: 'View email delivery and activity logs',
          icon: ICONS.email,
        },
      ],
    },
    {
      id: 'workflows',
      title: 'Workflows & Rules',
      accentClass: 'ss-settings-mega__column--workflows',
      headerIcon: ICONS.calendar,
      links: [
        {
          to: '/settings/workflow',
          title: 'Workflow',
          description: 'Create and manage approval workflows',
          icon: ICONS.calendar,
        },
        {
          to: '/settings/mileage',
          title: 'Mileage',
          description: 'Configure mileage reimbursement rules',
          icon: ICONS.car,
        },
        {
          to: `/settings${allRoutes.backupApprovers}`,
          title: 'Re-Assign Expense',
          description: 'Configure expense re-assignment rules',
          icon: ICONS.sync,
        },
      ],
      groups: workflowGroups,
    },
    {
      id: 'lookups',
      title: 'Org Lookups',
      accentClass: 'ss-settings-mega__column--lookups',
      headerIcon: ICONS.layers,
      links: [
        { to: '/settings/vendor', title: 'Vendor', description: 'Manage vendor master', icon: ICONS.clipboard },
        {
          to: '/settings/categories',
          title: 'Categories',
          description: 'Manage expense categories',
          icon: ICONS.grid,
        },
        { to: '/settings/locations', title: 'Locations', description: 'Manage locations', icon: ICONS.mapPin },
        {
          to: '/settings/departments',
          title: 'Departments',
          description: 'Manage departments',
          icon: ICONS.rocket,
        },
        {
          to: '/settings/designations',
          title: 'Designations',
          description: 'Manage job designations',
          icon: ICONS.searchUser,
        },
        { to: '/settings/division', title: 'Divisions', description: 'Manage divisions', icon: ICONS.barChart },
        { to: '/settings/countries', title: 'Countries', description: 'Manage countries', icon: ICONS.globe },
      ],
    },
    {
      id: 'anomalies',
      title: 'Expense Anomalies & Violations',
      accentClass: 'ss-settings-mega__column--anomalies',
      headerIcon: ICONS.shield,
      links: anomalyLinks,
    },
    {
      id: 'automations',
      title: 'Automations',
      accentClass: 'ss-settings-mega__column--automations',
      headerIcon: ICONS.lightning,
      links: [
        {
          to: `/settings/automation${allRoutes.SSToBCSyncFeeds}`,
          title: 'Automation - SS To BC Feeds',
          description: 'Configure automation rules for BC feeds',
          icon: ICONS.sync,
        },
      ],
      groups: [
        {
          title: 'Automation - CC Feed',
          items: [
            {
              to: `/settings/automation${allRoutes.bankCCFeedFiles}`,
              title: 'Bank CC Feed Files',
              description: 'Import and manage CC feed files',
              icon: ICONS.calendar,
            },
            {
              to: `/settings/automation${allRoutes.bankCCFeedMissingFiles}`,
              title: 'Bank CC Feed Files - Missing Imports',
              description: 'View missing CC feed imports',
              icon: ICONS.fileMissing,
            },
            {
              to: `/settings/automation${allRoutes.bankCCFeeds}`,
              title: 'Bank CC Feeds',
              description: 'View and manage bank CC feeds',
              icon: ICONS.creditCard,
            },
            {
              to: `/settings/automation${allRoutes.userCCFeeds}`,
              title: 'CC Feeds LineItems',
              description: 'Manage CC feed line items',
              icon: ICONS.fileAdd,
            },
            {
              to: `/settings/automation${allRoutes.userCCFeedsReconciliation}`,
              title: 'Feeds Line Items Reconciliation',
              description: 'Reconcile CC feed line items',
              icon: ICONS.barChart,
            },
            {
              to: `/settings/automation${allRoutes.mergeLineItems}`,
              title: 'Merge BC - SS LineItems',
              description: 'Merge BC and SS line items',
              icon: ICONS.merge,
            },
          ],
        },
      ],
    },
  ]
}

const buildSuperAdminColumns = (isSurveysModuleEnabled?: boolean | null): MenuColumn[] => {
  const groups: MenuGroup[] = []

  if (isSurveysModuleEnabled !== false) {
    groups.push({
      title: 'Surveys',
      items: [
        {
          to: '/settings/survey-dashboard',
          title: 'Survey Dashboard',
          description: 'View survey analytics and results',
          icon: ICONS.pieChart,
        },
        {
          to: '/settings/survey-campaigns',
          title: 'Survey Campaigns',
          description: 'Create and manage survey campaigns',
          icon: ICONS.campaigns,
        },
      ],
    })
  }

  return [
    {
      id: 'settings',
      title: 'Settings',
      accentClass: 'ss-settings-mega__column--settings',
      headerIcon: ICONS.settingsGear,
      links: [
        {
          to: '/settings/user-notifications',
          title: 'Maintenance / User Notifications',
          description: 'System maintenance and user alerts',
          icon: ICONS.alertCircle,
        },
      ],
      groups,
    },
  ]
}

const SettingsMegaMenu: React.FC<Props> = ({
  variant,
  roleID,
  isSurveysModuleEnabled,
  isExpenseAnomaliesModuleEnabled,
}) => {
  const columns =
    variant === 'superAdmin'
      ? buildSuperAdminColumns(isSurveysModuleEnabled)
      : buildOrgAdminColumns(roleID, isSurveysModuleEnabled, isExpenseAnomaliesModuleEnabled)

  return (
    <div
      className={clsx('ss-settings-mega', `ss-settings-mega--cols-${columns.length}`)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className='ss-settings-mega__grid'>
        {columns.map((col) => (
          <div key={col.id} className={clsx('ss-settings-mega__column', col.accentClass)}>
            <div className='ss-settings-mega__column-header'>
              <span className='ss-settings-mega__column-header-bg' aria-hidden='true'>
                <KTSVG path={col.headerIcon} className='svg-icon-3x' />
              </span>
              <span className='ss-settings-mega__column-header-label'>{col.title}</span>
            </div>
            <div className='ss-settings-mega__column-body'>
              {col.links.map((link) => (
                <MegaLink key={link.to + link.title} {...link} accentClass={col.accentClass} />
              ))}
              {col.groups?.map((group) => (
                <MegaGroup key={group.title} group={group} accentClass={col.accentClass} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export { SettingsMegaMenu }
